import React from 'react';
import {
	Control,
	FieldErrors,
	FieldValues,
	useController,
	UseFormRegister,
	UseFormWatch,
} from 'react-hook-form';

import { Label, Meta, NumberInput, RadioButton, Select } from '@components/common';
import { IFieldsAlarmManagementForm } from '@components/ModalComponents/AlarmManagementForm/AlarmManagementForm';

import { EFrequency, ERenotify } from '@enums/alarms';
import { EYupErrorTypes } from '@enums/common';
import { EAlarmManagementFields } from '@components/ModalComponents/AlarmManagementForm/enums';

import { hoursOptions, minutesOptions } from '@constants/timeOptions';
import {
	MAX_NUMBER_OF_REPETITIONS,
	MIN_NUMBER_OF_REPETITIONS,
	STEP_NUMBER_OF_REPETITIONS,
} from '@components/ModalComponents/AlarmManagementForm/constants';

import styles from './Schedule.module.scss';
import stylesLabel from '@components/ModalComponents/AlarmManagementForm/AlarmManagementForm.module.scss';

interface IScheduleProps {
	watch: UseFormWatch<IFieldsAlarmManagementForm>;
	register: UseFormRegister<FieldValues>;
	errors: FieldErrors<IFieldsAlarmManagementForm>;
	control: Control;
}

const DEFAULT_MANDATORY_ERROR = 'All parameters are mandatory';
const DEFAULT_INVALID_VALUE_ERROR = 'Invalid value';

const Schedule = ({
	watch,
	register,
	errors,
	control,
}: IScheduleProps) => {
	const numberOfRepetitions = watch(EAlarmManagementFields.numberOfRepetitions);
	const inRepetitions = watch(EAlarmManagementFields.frequency) === EFrequency.inRepetitions;
	const afterSincePrevious = watch(EAlarmManagementFields.renotify) === ERenotify.afterSincePrevious;

	const numberOfRepetitionsMaxError = errors.numberOfRepetitions?.type === EYupErrorTypes.max
		&& errors.numberOfRepetitions?.message;

	const numberOfRepetitionsMandatoryError = (
		errors.numberOfRepetitions?.type === EYupErrorTypes.required
		|| errors.repetitionHours?.type === EYupErrorTypes.required
		|| errors.repetitionMinutes?.type === EYupErrorTypes.required
	) && DEFAULT_MANDATORY_ERROR;

	const numberOfRepetitionsInvalidValueError = errors.repetitionMinutes?.value?.type === EYupErrorTypes.min
		&& DEFAULT_INVALID_VALUE_ERROR;

	const renotifyMandatoryError = (errors.renotifyHours?.type === EYupErrorTypes.required
		|| errors.renotifyMinutes?.type === EYupErrorTypes.required) && DEFAULT_MANDATORY_ERROR;

	const renotifyInvalidValueError = errors.renotifyMinutes?.value?.type === EYupErrorTypes.min
		&& DEFAULT_INVALID_VALUE_ERROR;

	const frequencyBlockError = numberOfRepetitionsMaxError
		|| numberOfRepetitionsInvalidValueError
		|| numberOfRepetitionsMandatoryError
		|| errors.frequency?.message;

	const renotifyBlockError = renotifyInvalidValueError || renotifyMandatoryError || errors.renotify?.message;

	const numberOfRepetitionsController = useController({
		name: EAlarmManagementFields.numberOfRepetitions,
		control,
	});

	const onChangeNumberOfRepetitions = (value: string) => {
		numberOfRepetitionsController.field.onChange(value);
	};

	return (
		<div className={styles.scheduleContainer}>
			<div className={styles.radioSection}>
				<div>
					<Label
						className={stylesLabel.label}
						label='Frequency'
						required
					/>
					<RadioButton
						id={EFrequency.immediately}
						isError={!!errors.frequency}
						label='Immediately, as soon as alarm event happens'
						name={EAlarmManagementFields.frequency}
						register={register}
						value={EFrequency.immediately}
						withUseForms
					/>
				</div>
				<div className={styles.timeSection}>
					<RadioButton
						id={EFrequency.inRepetitions}
						isError={!!errors.frequency}
						label='Once in'
						name={EAlarmManagementFields.frequency}
						register={register}
						value={EFrequency.inRepetitions}
						withUseForms
					/>
					<div className={styles.numberInput}>
						<NumberInput
							disabled={!inRepetitions}
							isClearable={false}
							isError={!!errors.numberOfRepetitions}
							maxValue={MAX_NUMBER_OF_REPETITIONS}
							minValue={MIN_NUMBER_OF_REPETITIONS}
							name={EAlarmManagementFields.numberOfRepetitions}
							onBlur={numberOfRepetitionsController.field.onBlur}
							onChange={onChangeNumberOfRepetitions}
							placeholder='number'
							step={STEP_NUMBER_OF_REPETITIONS}
							value={numberOfRepetitions?.toString()}
						/>
					</div>
					<div>repetitions in a</div>
					<div className={styles.timeSelect}>
						<Select
							control={control}
							disabled={!inRepetitions}
							isError={!!errors.repetitionHours}
							name={EAlarmManagementFields.repetitionHours}
							options={hoursOptions}
							placeholder='h'
						/>
					</div>
					<div>h</div>
					<div className={styles.timeSelect}>
						<Select
							control={control}
							disabled={!inRepetitions}
							isError={!!errors.repetitionMinutes}
							name={EAlarmManagementFields.repetitionMinutes}
							options={minutesOptions}
							placeholder='min'
						/>
					</div>
					<div>min</div>
				</div>
				{frequencyBlockError && <Meta message={frequencyBlockError} />}
			</div>
			<div className={styles.radioSection}>
				<div>
					<Label
						className={stylesLabel.label}
						label='Allow to renotify'
						required
					/>
					<RadioButton
						id={ERenotify.doNotNotify}
						isError={!!errors.renotify}
						label='Do not renotify'
						name={EAlarmManagementFields.renotify}
						register={register}
						value={ERenotify.doNotNotify}
						withUseForms
					/>
				</div>
				<RadioButton
					id={ERenotify.whenOccursAgain}
					isError={!!errors.renotify}
					label='When occurs again'
					name={EAlarmManagementFields.renotify}
					register={register}
					value={ERenotify.whenOccursAgain}
					withUseForms
				/>
				<div className={styles.timeSection}>
					<RadioButton
						id={ERenotify.afterSincePrevious}
						isError={!!errors.renotify}
						label='After'
						name={EAlarmManagementFields.renotify}
						register={register}
						value={ERenotify.afterSincePrevious}
						withUseForms
					/>
					<div className={styles.timeSection}>
						<div className={styles.timeSelect}>
							<Select
								control={control}
								disabled={!afterSincePrevious}
								isError={!!errors.renotifyHours}
								name={EAlarmManagementFields.renotifyHours}
								options={hoursOptions}
								placeholder='h'
							/>
						</div>
						<div>h</div>
						<div className={styles.timeSelect}>
							<Select
								control={control}
								disabled={!afterSincePrevious}
								isError={!!errors.renotifyMinutes}
								name={EAlarmManagementFields.renotifyMinutes}
								options={minutesOptions}
								placeholder='min'
							/>
						</div>
						<div>min since previous alert was sent</div>
					</div>
				</div>
				{renotifyBlockError && <Meta message={renotifyBlockError} />}
			</div>
		</div>
	);
};

export default Schedule;
